import React, { useState } from "react";
import { Button, TextInput, Group, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ArrowBackUp, Plus, Trash } from "tabler-icons-react";
import { FlexFormSelectOption, OptionsMetadata } from "@captiva/shared-types";

interface StaticOptionsEditorProps {
  options: FlexFormSelectOption[];
  setOptions: (options: FlexFormSelectOption[]) => void;
}

export default function StaticOptionsEditor({ options, setOptions }: StaticOptionsEditorProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [pendingMetadata, setPendingMetadata] = useState<Record<string, OptionsMetadata>>({});
  const [showMetaForm, setShowMetaForm] = useState(false);
  const [metaKey, setMetaKey] = useState("");
  const [metaLabel, setMetaLabel] = useState("");
  const [metaValue, setMetaValue] = useState("");

  const addOption = () => {
    if (!label || !value) return;
    setOptions([
      ...options,
      {
        label,
        value,
        metadata: Object.keys(pendingMetadata).length ? pendingMetadata : undefined,
      },
    ]);
    setLabel("");
    setValue("");
    setPendingMetadata({});
    setShowMetaForm(false);
  };
  const removeOption = (idx: number) => setOptions(options.filter((_, i) => i !== idx));

  // Metadata for the option being created
  const addMeta = () => {
    if (!metaKey || !metaLabel) return;
    setPendingMetadata({ ...pendingMetadata, [metaKey]: { label: metaLabel, value: metaValue } });
    setMetaKey("");
    setMetaLabel("");
    setMetaValue("");
  };
  const removeMeta = (key: string) => {
    const newMeta = { ...pendingMetadata };
    delete newMeta[key];
    setPendingMetadata(newMeta);
  };

  // Edit metadata for an existing option
  const editOptionMeta = (idx: number, newMeta: Record<string, OptionsMetadata> | undefined) => {
    setOptions(options.map((opt, i) => i === idx ? { ...opt, metadata: newMeta } : opt));
  };

  // State for per-option metadata form
  const [editingMetaIdx, setEditingMetaIdx] = useState<number | null>(null);
  const [editMetaKey, setEditMetaKey] = useState("");
  const [editMetaLabel, setEditMetaLabel] = useState("");
  const [editMetaValue, setEditMetaValue] = useState("");

  return (
    <div>
      {options.map((opt, idx) => (
        <Group
          key={idx}
          mb={8}
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: 4,
            padding: 8,
            alignItems: "flex-start",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 120 }}>
            <Text size="sm"><b>Label:</b> {opt.label}</Text>
            <Text size="sm"><b>Value:</b> {opt.value}</Text>
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            {opt.metadata && Object.keys(opt.metadata).length ? (
              <div style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 4 }}>
                <Table>
                  <thead>
                    <tr>
                      <th><Text size="xs">Meta Key</Text></th>
                      <th><Text size="xs">Label</Text></th>
                      <th><Text size="xs">Value</Text></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(opt.metadata).map(([k, v]) => (
                      <tr key={k}>
                        <td><Text size="xs">{k}</Text></td>
                        <td><Text size="xs">{v.label}</Text></td>
                        <td><Text size="xs">{String(v.value ?? "")}</Text></td>
                        <td>
                          <Button
                            size={isMobile ? "md" : "xs"}
                            color="red"
                            variant="subtle"
                            leftIcon={<Trash size={isMobile ? 18 : 16} />}
                            style={isMobile ? { minHeight: 44, minWidth: 44 } : undefined}
                            onClick={() => {
                              const newMeta = { ...opt.metadata };
                              delete newMeta[k];
                              editOptionMeta(idx, newMeta as any);
                            }}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : null}
            {/* Inline metadata form for this option */}
            {editingMetaIdx === idx ? (
              <Group mt={4} mb={4} spacing={isMobile ? "md" : 4} align={isMobile ? "stretch" : "flex-end"} style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: isMobile ? 8 : 4, flexDirection: isMobile ? "column" : "row" }}>
                <TextInput size={isMobile ? "md" : "xs"} label="Meta Key" value={editMetaKey} onChange={e => setEditMetaKey(e.target.value)} style={{ minWidth: 60 }} />
                <TextInput size={isMobile ? "md" : "xs"} label="Meta Label" value={editMetaLabel} onChange={e => setEditMetaLabel(e.target.value)} style={{ minWidth: 60 }} />
                <TextInput size={isMobile ? "md" : "xs"} label="Meta Value" value={editMetaValue} onChange={e => setEditMetaValue(e.target.value)} style={{ minWidth: 60 }} />
                <Group spacing={isMobile ? 12 : 4} style={{ marginTop: isMobile ? 8 : 0 }}>
                  <Button
                    size={isMobile ? "md" : "sm"}
                    color="cap-blue"
                    variant="outline"
                    leftIcon={<Plus size={16} />}
                    style={{ minHeight: isMobile ? 44 : 36 }}
                    onClick={() => {
                      if (!editMetaKey || !editMetaLabel) return;
                      const newMeta = opt.metadata ? { ...opt.metadata } : {};
                      newMeta[editMetaKey] = { label: editMetaLabel, value: editMetaValue };
                      editOptionMeta(idx, newMeta as any);
                      setEditMetaKey("");
                      setEditMetaLabel("");
                      setEditMetaValue("");
                      setEditingMetaIdx(null);
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    size={isMobile ? "md" : "sm"}
                    variant="default"
                    color="gray"
                    leftIcon={<ArrowBackUp size={16} />}
                    style={{ minHeight: isMobile ? 44 : 36 }}
                    onClick={() => {
                      setEditMetaKey("");
                      setEditMetaLabel("");
                      setEditMetaValue("");
                      setEditingMetaIdx(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Group>
              </Group>
            ) : (
              <Button
                size={isMobile ? "md" : "sm"}
                color="cap-blue"
                variant="outline"
                style={{ alignSelf: 'center', marginTop: 4, minHeight: isMobile ? 44 : 36 }}
                leftIcon={<Plus size={16} />}
                onClick={() => {
                  setEditingMetaIdx(idx);
                  setEditMetaKey("");
                  setEditMetaLabel("");
                  setEditMetaValue("");
                }}
              >
                Metadata
              </Button>
            )}
          </div>
          <Button
            size={isMobile ? "md" : "sm"}
            color="red"
            variant="outline"
            onClick={() => removeOption(idx)}
            style={{ alignSelf: 'center', minHeight: isMobile ? 44 : undefined, marginLeft: isMobile ? 12 : 0 }}
          >
            Remove
          </Button>
        </Group>
      ))}
      <Group
        mb="xs"
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: 4,
          padding: 8,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "flex-end",
          justifyContent: "space-between",
          gap: isMobile ? 12 : undefined,
        }}
      >
        <TextInput
          size={isMobile ? "md" : "sm"}
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <TextInput
          size={isMobile ? "md" : "sm"}
          label="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        {!showMetaForm && (
          <Button
            size={isMobile ? "md" : "sm"}
            color="cap-blue"
            variant="outline"
            onClick={() => setShowMetaForm(true)}
            style={{ alignSelf: isMobile ? 'stretch' : 'center', marginTop: isMobile ? 0 : 24, minHeight: isMobile ? 44 : undefined }}
          >
            Add metadata
          </Button>
        )}
        {showMetaForm && (
          <Button
            size={isMobile ? "md" : "sm"}
            color="cap-blue"
            variant="outline"
            onClick={() => setShowMetaForm(false)}
            style={{ alignSelf: isMobile ? 'stretch' : 'flex-end', marginTop: isMobile ? 0 : 24, minHeight: isMobile ? 44 : undefined }}
          >
            Hide metadata
          </Button>
        )}
        {showMetaForm && (
          <div style={{ width: "100%", marginTop: isMobile ? 8 : 0 }}>
            <Group mb="xs" style={{ flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-end" }}>
              <TextInput size={isMobile ? "md" : "xs"} label="Meta Key" value={metaKey} onChange={(e) => setMetaKey(e.target.value)} error={!metaKey && metaKey !== '' ? 'Required' : undefined} />
              <TextInput size={isMobile ? "md" : "xs"} label="Meta Label" value={metaLabel} onChange={(e) => setMetaLabel(e.target.value)} error={!metaLabel && metaLabel !== '' ? 'Required' : undefined} />
              <TextInput size={isMobile ? "md" : "xs"} label="Meta Value" value={metaValue} onChange={(e) => setMetaValue(e.target.value)} error={!metaValue && metaValue !== '' ? 'Required' : undefined} />
              <Button
                color="cap-blue"
                variant="outline"
                size={isMobile ? "md" : "sm"}
                leftIcon={<Plus size={16} />}
                onClick={addMeta}
                disabled={!metaKey || !metaLabel || !metaValue}
                style={{ minHeight: isMobile ? 44 : 36 }}
              >
                Add metadata
              </Button>
            </Group>
            <Table>
              <thead>
                <tr><th>Meta Key</th><th>Label</th><th>Value</th><th></th></tr>
              </thead>
              <tbody>
                {Object.entries(pendingMetadata).map(([key, meta]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{meta.label}</td>
                    <td>{String(meta.value ?? "")}</td>
                    <td>
                      <Button
                        size={isMobile ? "md" : "sm"}
                        color="red"
                        variant="outline"
                        leftIcon={<Trash size={16} />}
                        onClick={() => removeMeta(key)}
                        style={{ minHeight: isMobile ? 44 : 36, minWidth: isMobile ? 44 : 36 }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <Button
          color="cap-blue"
          variant="outline"
          size={isMobile ? "md" : "sm"}
          onClick={addOption}
          leftIcon={<Plus size={16} />}
          style={{ alignSelf: isMobile ? 'stretch' : 'flex-end', minHeight: isMobile ? 44 : undefined }}
        >
          Add
        </Button>
      </Group>
    </div>
  );
}
